import styled from "styled-components";

import { DataTable, ExtendedColumnDef } from "components/Datatable";
import { getPagesToShow } from "@/utils/pagination";
import type { Meta } from "src/@types/paginated";
import type { IDepositStoresSummaryRow } from "src/@types/deposit-reconciliation";

import { useDepositStoresFilterActions } from "../../ducks/filter/hooks/actions";
import PageSizeSelect from "../../../components/page-size-select";

interface ITableProps {
  data: IDepositStoresSummaryRow[];
  columns: ExtendedColumnDef<IDepositStoresSummaryRow>[];
  page: number;
  pageSize: number;
  total: number;
  isFetching?: boolean;
}

export default function DepositStoresTable({
  data,
  columns,
  page,
  pageSize,
  total,
  isFetching = false,
}: ITableProps) {
  const { updateFilter } = useDepositStoresFilterActions();

  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const meta: Meta = {
    currentPage: page,
    lastPage,
    perPage: pageSize,
    total,
    prev: page > 1 ? page - 1 : null,
    next: page < lastPage ? page + 1 : null,
  };

  const goToPage = (newPage: number) =>
    updateFilter({ key: "meta", value: { page: newPage } });

  const handlePageSizeChange = (size: number) => {
    updateFilter({ key: "pageSize", value: size });
    updateFilter({ key: "meta", value: { page: 1 } });
  };

  return (
    <div>
      <DataTable
        columns={columns}
        data={data}
        isLoading={isFetching}
        emptyMessage="Nenhuma loja encontrada no período."
      />

      <div className="flex w-full items-center justify-between pagination-container">
        <PaginationButtonsContainer>
          {data.length !== 0 && (
            <>
              <button
                className={`navigate-btn previous ${
                  meta.prev !== null && !isFetching ? "" : "disabled"
                }`}
                onClick={() => meta.prev !== null && goToPage(meta.prev)}
                disabled={meta.prev === null || isFetching}
              >
                Anterior
              </button>
              {getPagesToShow(meta).map((p, idx) => (
                <span
                  key={`${p}-${idx}`}
                  className={`pages ${p === meta.currentPage ? "selected" : ""} ${
                    p === "..." ? "ocult" : ""
                  }`}
                  onClick={() => typeof p === "number" && goToPage(p)}
                >
                  {p}
                </span>
              ))}
              <button
                className={`navigate-btn next ${
                  meta.next !== null && !isFetching ? "" : "disabled"
                }`}
                onClick={() => meta.next !== null && goToPage(meta.next)}
                disabled={meta.next === null || isFetching}
              >
                Próximo
              </button>
            </>
          )}
        </PaginationButtonsContainer>
        <div className="flex items-center gap-4">
          <PageSizeSelect value={pageSize} onChange={handlePageSizeChange} />
          <span className="text-sm font-light leading-4 text-[#444444]">
            {page} de {lastPage} página{lastPage !== 1 ? "s" : ""} · {total}{" "}
            {total === 1 ? "loja" : "lojas"}
          </span>
        </div>
      </div>
    </div>
  );
}

const PaginationButtonsContainer = styled.div`
  display: flex;
  font-size: 14px;

  .pages {
    display: flex;
    justify-content: center;
    padding: 8px 6px;
    border: 1px solid #dcdce3;
    border-left: none;
    border-top: none;
    min-width: 29px;

    &.selected {
      background-color: #f4c83e;
    }
  }

  .pages:not(.ocult) {
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .navigate-btn {
    cursor: pointer;
    padding: 6px 12px;
    background-color: #2b0c1a;
    color: #fff;

    &.previous {
      border-radius: 0 0 0 8px;
    }

    &.next {
      border-radius: 0 0 8px 0;
    }

    &:hover {
      text-decoration: underline;
    }

    &.disabled {
      color: #8d91a2;
      background-color: #d9dae0;
      cursor: not-allowed;
      text-decoration: none;
    }
  }
`;
